import {
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import DisplayErrorPopup from './DisplayError'
import { ChangeEvent, useState } from 'react'
import CustomDialog from './CustomDialog'
import { RootState } from '../store'
import {
  architectureRoles,
  AttributeActionNames,
  AttributeResourceNames,
  engineeringRoles,
  UserRole,
  UserRoleNames,
} from '../models/AccessControl'
import { Can } from '../utils/AbilityUtil'
import { addUserAsync } from '../services/usersService'
import { showErrorMessage } from '../actions/organization'
import { createSelector } from '@reduxjs/toolkit'
import { InviteUserIcon } from './Svgs'
import CustomDropdown from './CustomDropdown'

const organizationIdSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedOrganization?.id,
)
const organizationDomainSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.selectedOrganization?.domain,
)
const userRolesSelector = createSelector(
  [(state: RootState) => state.organization],
  (organization) => organization.userRoles,
)
const userSelector = createSelector(
  [(state: RootState) => state.auth],
  (auth) => auth.user,
)

interface InviteUserProps {
  isOpen: boolean
  handleClose: () => void
}

const InviteUserComponent: React.FC<InviteUserProps> = ({
  isOpen,
  handleClose,
}) => {
  const dispatch = useDispatch<any>()
  const [userEmail, setUserEmail] = useState<string>()
  const [userRole, setUserRole] = useState<UserRole | null>(null)
  const [inviteSent, setInviteSent] = useState<boolean>(false)
  const organizationId = useSelector(organizationIdSelector) ?? ''
  const organizationDomain = useSelector(organizationDomainSelector) ?? ''
  const userRoles = useSelector(userRolesSelector) ?? []
  const user = useSelector(userSelector)

  const filteredUserRoles = !!user
    ? architectureRoles.includes(user.role.name)
      ? userRoles.filter(
          (userRole) =>
            architectureRoles.includes(userRole.name) ||
            userRole.name === UserRoleNames.Viewer,
        )
      : engineeringRoles.includes(user.role.name)
        ? userRoles.filter(
            (userRole) =>
              engineeringRoles.includes(userRole.name) ||
              userRole.name === UserRoleNames.Viewer,
          )
        : userRoles
    : userRoles

  const isUserEmailValid = () => {
    return userEmail && userEmail.includes(organizationDomain)
  }

  const handleUserRoleClick = (userRole: UserRole) => {
    setUserRole(userRole)
  }

  const handleUserEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setUserEmail(event?.target.value)
  }

  const handleInviteClick = () => {
    if (!userEmail || !isUserEmailValid()) {
      dispatch(showErrorMessage('email must be from same company'))
      return
    }

    dispatch(
      addUserAsync({
        organization_id: Number(organizationId),
        role_id: userRole ? userRole.id : null,
        email: userEmail,
      }),
    )
    setInviteSent(true)
    setTimeout(() => handleClose(), 3000)
  }

  return (
    <Can I={AttributeActionNames.Create} a={AttributeResourceNames.Users}>
      <CustomDialog
        isOpen={isOpen}
        title=''
        hideActions={true}
        handleClose={handleClose}
      >
        <DisplayErrorPopup />
        <Stack
          rowGap={'1rem'}
          sx={{
            minHeight: '7rem',
            boxSizing: 'border-box',
            width: '30rem',
            justifyContent: 'center',
          }}
        >
          {!inviteSent ? (
            <>
              <Box sx={{ display: 'flex', gap: '0.75rem', ml: '0.5rem' }}>
                <InviteUserIcon color='#04002E' />
                <Typography
                  sx={{
                    color: '#04002E',
                    fontSize: '0.94rem',
                    fontWeight: 500,
                    lineHeight: 'normal',
                  }}
                >
                  Invite to Team
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', width: '85%' }}>
                  <Stack width='100%' sx={{ ml: '0.5rem', gap: '0.75rem' }}>
                    <Box sx={{ display: 'flex', width: '98%' }}>
                      <Box sx={{ display: 'flex', width: '50%' }}>
                        <Typography
                          sx={{
                            color: '#797979',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            lineHeight: 'normal',
                          }}
                        >
                          Invite
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', width: '50%' }}>
                        <Typography
                          sx={{
                            color: '#797979',
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            lineHeight: 'normal',
                          }}
                        >
                          Role Assigned
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        width: '98%',
                        border: '1px solid #97999B',
                        background: '#F5F5F5',
                        borderRadius: '0.4rem',
                      }}
                    >
                      <TextField
                        sx={{
                          width: '50%',
                          mt: 0,
                          mb: 0,
                          '& .MuiInputBase-input': {
                            color: '#04002E',
                            fontSize: '0.7rem',
                            lineHeight: 'normal',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#04002E',
                            opacity: 1,
                            fontSize: '0.7rem',
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                        }}
                        size='small'
                        placeholder='Add email'
                        required
                        margin='dense'
                        id='email'
                        name='email'
                        value={userEmail}
                        onChange={handleUserEmailChange}
                      />
                      <Divider
                        orientation='vertical'
                        sx={{ background: '#97999B' }}
                      />
                      <Box
                        sx={{
                          px: '0.5rem',
                          width: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CustomDropdown
                          title={!!userRole ? userRole.name : 'Select role'}
                          color='#4F4F4F'
                          buttonStyleProps={{
                            justifyContent: 'space-between',
                            padding: 0,
                            background: 'transparent',
                            textTransform: 'capitalize',
                            width: '100%',
                          }}
                          titleStyleProps={{
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            textTransform: 'capitalize',
                          }}
                        >
                          {filteredUserRoles.map((item, index) => (
                            <MenuItem
                              key={index}
                              onClick={() => handleUserRoleClick(item)}
                              sx={{
                                color: '#04002E',
                                '&:hover': {
                                  backgroundColor: '#1705E5',
                                  color: '#fff',
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: '12px',
                                  textTransform: 'capitalize',
                                }}
                              >
                                {item.name.toLowerCase()}
                              </Typography>
                            </MenuItem>
                          ))}
                        </CustomDropdown>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
                <Box sx={{ display: 'flex', flex: 1 }}>
                  <Button
                    disableRipple
                    type='submit'
                    size='large'
                    onClick={handleInviteClick}
                    sx={{
                      width: '100%',
                      fontSize: '0.7rem',
                      background: '#CDF765',
                      color: '#04002E',
                      textTransform: 'capitalize',
                    }}
                  >
                    Invite
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Stack
              gap='0.75rem'
              height='100%'
              alignItems='center'
              justifyContent='center'
            >
              <InviteUserIcon color='#04002E' />
              <Typography
                sx={{
                  color: '#04002E',
                  fontSize: '0.94rem',
                  fontWeight: 500,
                  lineHeight: 'normal',
                }}
              >
                Team member has been added
              </Typography>
            </Stack>
          )}
        </Stack>
      </CustomDialog>
    </Can>
  )
}

export default InviteUserComponent
